document.addEventListener('DOMContentLoaded', function () {
  /**
   * Build the API URL for a postcode search.
   *
   * @param {string} code - The postcode from the form.
   * @returns {string}
   */
  const buildLocationLookupUrl = (code) => `https://nominatim.openstreetmap.org/search?postalcode=${code}&format=json`;

  /**
   * Store location with map coordinates.
   *
   * @typedef {Object} PositionStore
   * @property {string} name - Store name.
   * @property {number} lat - Store latitude.
   * @property {number} lon - Store longitude.
   */

  /**
   * A postcode rule from the data file.
   *
   * @typedef {Object} PostcodeRule
   * @property {string} postcode - Main postcode label.
   * @property {string} wildcard - Prefix or range pattern.
   * @property {string} area - Area name.
   * @property {string[]} store - Store names for this area.
   */

  /**
   * Matched postcode data.
   *
   * @typedef {Object} PostcodeMatch
   * @property {string} input - Clean postcode from the form.
   * @property {string} outwardCode - First part of the postcode.
   * @property {string} postcode - Main postcode label.
   * @property {string} wildcard - Prefix or range pattern.
   * @property {string} area - Area name.
   * @property {string[]} store - Store names for this area.
   */

  /**
   * User coordinates from the postcode lookup.
   *
   * @typedef {Object} Coordinates
   * @property {number} lat - Latitude value.
   * @property {number} lon - Longitude value.
   */

  /**
   * Store with distance to the user.
   *
   * @typedef {PositionStore & { distanceKm: number }} NearestStore
   */

  /**
   * Wildcard pattern data.
   *
   * @typedef {{ type: 'prefix', value: string } | { type: 'range', start: string, end: string }} WildcardPattern
   */

  /** @type {PositionStore[]} */
  const STORE_LOCATIONS = [
    {
      name: 'eastbourne',
      lat: 50.79603,
      lon: 0.28181,
    },
    {
      name: 'milton keynes',
      lat: 52.0063496,
      lon: -0.7195133,
    },
    {
      name: 'newcastle',
      lat: 55.0122567,
      lon: -1.662898,
    },
    {
      name: 'reigate',
      lat: 51.24298,
      lon: -0.21578,
    },
  ];

  const errorNotification = window.postalcodeErrors || {};

  // Postcode helpers

  /** @type {Promise<PostcodeRule[]> | null} */
  let rulesPromise = null;

  /**
   * Clean a postcode and keep one space between parts.
   *
   * @param {string} value - Raw postcode text.
   * @returns {string}
   */
  function normalizePostcode(value) {
    return String(value || '')
      .toUpperCase()
      .trim()
      .replace(/\s+/g, ' ');
  }

  /**
   * Remove spaces from a postcode.
   *
   * @param {string} value - Raw postcode text.
   * @returns {string}
   */
  function compactPostcode(value) {
    return normalizePostcode(value).replace(/\s+/g, '');
  }

  /**
   * Get the first part of a UK postcode.
   *
   * @param {string} input - Postcode from the user.
   * @returns {string}
   */
  function extractOutwardCode(input) {
    const compact = compactPostcode(input);

    if (/^\w{5,7}$/.test(compact) && /\d[A-Z]{2}$/.test(compact)) {
      return compact.slice(0, -3);
    }

    const match = compact.match(/^([A-Z]{1,2}\d[A-Z\d]?)/);
    return match ? match[1] : compact;
  }

  /**
   * Split a code into letters and numbers.
   *
   * @param {string} code - Code to split.
   * @returns {string[]}
   */
  function tokenizeCode(code) {
    return (
      String(code || '')
        .toUpperCase()
        .match(/[A-Z]+|\d+/g) || []
    );
  }

  /**
   * Compare two strings with letters and numbers.
   *
   * @param {string} left - First value.
   * @param {string} right - Second value.
   * @returns {number}
   */
  function compareAlphaNum(left, right) {
    const a = tokenizeCode(left);
    const b = tokenizeCode(right);
    const length = Math.max(a.length, b.length);

    for (let index = 0; index < length; index += 1) {
      const x = a[index];
      const y = b[index];

      if (x === undefined) return -1;
      if (y === undefined) return 1;
      if (x === y) continue;

      const xIsNumber = /^\d+$/.test(x);
      const yIsNumber = /^\d+$/.test(y);

      if (xIsNumber && yIsNumber) {
        return Number(x) - Number(y);
      }

      if (x < y) return -1;
      if (x > y) return 1;
    }

    return 0;
  }

  /**
   * Turn a wildcard rule into pattern data.
   *
   * @param {string} wildcard - Wildcard from the data file.
   * @returns {WildcardPattern}
   */
  function parseWildcardPattern(wildcard) {
    const pattern = compactPostcode(wildcard).replace(/\*/g, '');
    const parts = pattern.split('-');

    if (parts.length === 1) {
      return {
        type: 'prefix',
        value: parts[0],
      };
    }

    return {
      type: 'range',
      start: parts[0],
      end: parts[1],
    };
  }

  /**
   * Check if the outward code matches a rule.
   *
   * @param {string} outwardCode - Outward part of the postcode.
   * @param {{ wildcard: string }} rule - Rule to test.
   * @returns {boolean}
   */
  function matchRule(outwardCode, rule) {
    if (!rule || !rule.wildcard) {
      return false;
    }

    const parsed = parseWildcardPattern(rule.wildcard);

    if (parsed.type === 'prefix') {
      return outwardCode.startsWith(parsed.value);
    }

    return compareAlphaNum(outwardCode, parsed.start) >= 0 && compareAlphaNum(outwardCode, parsed.end) <= 0;
  }

  /**
   * Get a readable message from an unknown error.
   *
   * @param {unknown} error - Error value from catch.
   * @param {string} fallbackMessage - Message for unknown cases.
   * @returns {string}
   */
  function getErrorMessage(error, fallbackMessage) {
    return error instanceof Error && error.message ? error.message : fallbackMessage;
  }

  /**
   * Load postcode rules from the data file.
   *
   * @param {string | undefined} dataUrl - URL from the element dataset.
   * @returns {Promise<PostcodeRule[]>}
   */
  function loadPostcodeRules(dataUrl) {
    if (!dataUrl) {
      throw new Error(errorNotification.dataSourceMissing);
    }

    if (!rulesPromise) {
      rulesPromise = fetch(dataUrl).then((response) => {
        if (!response.ok) {
          throw new Error(errorNotification.rulesLoadFailed);
        }

        return response.json();
      });
    }

    return rulesPromise;
  }

  /**
   * Find the first rule for the postcode.
   *
   * @param {string} postcode - Postcode from the form.
   * @param {PostcodeRule[]} rules - All postcode rules.
   * @returns {PostcodeMatch | null}
   */
  function findPostcodeEntry(postcode, rules) {
    const normalizedInput = normalizePostcode(postcode);
    const outwardCode = extractOutwardCode(normalizedInput);

    if (!outwardCode) {
      return null;
    }

    const entry = rules.find((rule) => matchRule(outwardCode, rule)) || null;

    if (!entry) {
      return null;
    }

    return {
      input: normalizedInput,
      outwardCode,
      postcode: entry.postcode,
      wildcard: entry.wildcard,
      area: entry.area,
      store: entry.store,
    };
  }

  /**
   * Send a custom event from the document.
   *
   * @param {string} eventName - Event name.
   * @param {unknown} detail - Event data.
   * @returns {void}
   */
  function emitPostalCodeEvent(eventName, detail) {
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
      }),
    );
  }

  // Geo helpers

  /**
   * Get coordinates for a postcode from the map service.
   *
   * @param {string} code - Clean postcode value.
   * @returns {Promise<Coordinates>}
   */
  async function getCoordinates(code) {
    const apiEndPoint = buildLocationLookupUrl(code);
    const response = await fetch(apiEndPoint);

    if (!response.ok) {
      throw new Error(errorNotification.locationLookupFailed);
    }

    const data = await response.json();

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error(errorNotification.postcodeNotFoundOnMap);
    }

    const lat = Number(data[0].lat);
    const lon = Number(data[0].lon);

    if (Number.isNaN(lat) || Number.isNaN(lon)) {
      throw new Error(errorNotification.invalidCoordinates);
    }

    return { lat, lon };
  }

  /**
   * Calculate the distance between two points in kilometres.
   *
   * @param {number} lat1 - First latitude.
   * @param {number} lon1 - First longitude.
   * @param {number} lat2 - Second latitude.
   * @param {number} lon2 - Second longitude.
   * @returns {number}
   */
  function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const toRad = (deg) => (deg * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /**
   * Find the nearest store for the user location.
   *
   * @param {Coordinates} userLocation - Coordinates from the postcode.
   * @param {PositionStore[]} stores - All available stores.
   * @returns {NearestStore | null}
   */
  function findNearestStore(userLocation, stores) {
    if (stores.length === 0) {
      return null;
    }

    /** @type {NearestStore | null} */
    let nearestStore = null;
    let minDistance = Infinity;

    for (const store of stores) {
      const distance = haversineDistance(userLocation.lat, userLocation.lon, store.lat, store.lon);

      if (distance < minDistance) {
        minDistance = distance;
        nearestStore = {
          ...store,
          distanceKm: Number(distance.toFixed(2)),
        };
      }
    }

    return nearestStore;
  }

  class PostalCodeForm extends HTMLElement {
    constructor() {
      super();

      /** @type {HTMLFormElement | null} */
      this.form = null;

      /** @type {HTMLParagraphElement | null} */
      this.errorContainer = null;

      /** @type {HTMLScriptElement | null} */
      this.scriptConfig = null;

      /** @type {Array<PostcodeRule>} */
      this.codes = [];

      /** @type {(event: SubmitEvent) => void} */
      this.boundOnSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.errorContainer = this.querySelector('.postal-code__form-error');
      this.scriptConfig = this.querySelector('script[data-postal-codes]');
      this.codes = this.getConfigFromScript();

      this.form?.addEventListener('submit', this.boundOnSubmit);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.boundOnSubmit);
    }

    /**
     * Get json form the shop metafield.
     *
     * @returns {Array<PostcodeRule>}
     */
    getConfigFromScript() {
      try {
        return JSON.parse(this.scriptConfig.textContent);
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    /**
     * Show a short notification message.
     *
     * @param {string} notification - Text for the user.
     * @param {"found" | "error"} type - Notification style.
     * @returns {void}
     */
    showNotification(notification, type) {
      if (!notification || !(this.errorContainer instanceof HTMLParagraphElement)) return;
      const classList = ['postal-code__form-error--show', `postal-code__form-error--${type}`];
      if (this.errorContainer.dismissTimeout) clearTimeout(this.errorContainer.dismissTimeout);

      this.errorContainer.textContent = notification;
      this.errorContainer.classList.add(...classList);

      this.errorContainer.dismissTimeout = setTimeout(() => {
        this.errorContainer.textContent = '';
        this.errorContainer.classList.remove(...classList);
      }, 2000);
    }

    /**
     * Handle the form submit event.
     *
     * @param {SubmitEvent} event - Submit event from the form.
     * @returns {Promise<void>}
     */
    async onSubmit(event) {
      event.preventDefault();

      if (!(event.target instanceof HTMLFormElement)) {
        return;
      }

      const formData = new FormData(event.target);
      const code = formData.get('code');

      if (typeof code !== 'string' || !code.trim()) {
        this.showNotification(errorNotification.postcodeRequired);
        emitPostalCodeEvent('postal-code-form:error', {
          code: '',
          message: errorNotification.postcodeRequired,
        });
        return;
      }

      try {
        const dataUrl = this.dataset.source;
        const rules = this.codes.length == 0 ? await loadPostcodeRules(dataUrl) : this.codes;
        console.log(this.codes, rules);
        const result = findPostcodeEntry(code, rules);

        if (result === null) {
          this.showNotification(errorNotification.outsideDeliveryArea);

          emitPostalCodeEvent('postal-code-form:not-found', {
            code: normalizePostcode(code),
            message: errorNotification.outsideDeliveryArea,
          });
          return;
        }

        const coordinates = await getCoordinates(result.input);
        const nearestStore = findNearestStore(coordinates, STORE_LOCATIONS);
        const nearestStoreFromResult = result.store[0].toLocaleLowerCase();
        const otherStoreFromResult = result.store.slice(1).map((storeName) => storeName.toLocaleLowerCase());
        if (nearestStoreFromResult != nearestStore.name) {
          nearestStore.name = nearestStoreFromResult;
        }

        if (!nearestStore) {
          throw new Error(errorNotification.nearestStoreNotFound);
        }

        emitPostalCodeEvent('postal-code-form:submit', {
          ...result,
          coordinates,
          nearestStore,
          otherStoreFromResult,
        });
      } catch (error) {
        const message = getErrorMessage(error, errorNotification.lookupFailed);
        this.showNotification(message);

        emitPostalCodeEvent('postal-code-form:error', {
          code: normalizePostcode(code),
          message,
        });
      } finally {
        this.form?.reset();
      }
    }
  }

  /**
   * Drawer for postcode results.
   */
  class PostalCodeDrawer extends HTMLElement {
    constructor() {
      super();

      /** @type {NodeListOf<HTMLElement> | []} */
      this.closeElements = [];

      /** @type {(event?: Event) => void} */
      this.boundToggleDrawer = this.toggleDrawer.bind(this);

      /** @type {HTMLElement | null} */
      this.lastPosition = null;

      /** @type {HTMLElement | null} */
      this.nearestCard = null;

      /** @type {HTMLElement | null} */
      this.availableStore = null;
    }

    connectedCallback() {
      if (this.moved) return;

      this.moved = true;
      document.body.appendChild(this);

      this.closeElements = this.querySelectorAll('.postal-drawer__overlay, .postal-drawer__close');
      document.addEventListener('postal-code-form:submit', this.boundToggleDrawer);
      this.closeElements.forEach((element) => element.addEventListener('click', this.boundToggleDrawer));
      this.availableStore = this.querySelector('#available-store');
    }

    disconnectedCallback() {
      document.removeEventListener('postal-code-form:submit', this.boundToggleDrawer);
      this.closeElements.forEach((element) => element.removeEventListener('click', this.boundToggleDrawer));
    }

    /**
     * Open or close the drawer.
     *
     * @param {CustomEvent<PostcodeMatch & { coordinates: Coordinates, nearestStore: NearestStore, otherStoreFromResult: Array<string> }> | Event} [event] - Event from submit or click.
     * @returns {void}
     */
    toggleDrawer(event) {
      const isOpen = this.hasAttribute('open');

      /**
       * @param {Event} event
       * @returns {boolean}
       */
      const isCustomEvent = (event) => {
        return event instanceof CustomEvent && event.type === 'postal-code-form:submit';
      };

      if (isCustomEvent(event)) {
        const { nearestStore, otherStoreFromResult } = event.detail;
        if (nearestStore && nearestStore.name) {
          const nearestCard = this.findNearestCardStore(nearestStore.name);
          if (nearestCard) {
            this.lastPosition = nearestCard.closest('.postal-container__body-store');
            this.nearestCard = nearestCard;
            this.insertNearestCard(nearestCard);
            this.reorderAvalibleStores(otherStoreFromResult, false);
          }
        }
      }

      this.toggleAttribute('open', !isOpen);
      document.body.classList.toggle('overflow-hidden', !isOpen);

      if (isOpen && this.lastPosition && this.nearestCard) {
        this.lastPosition.appendChild(this.nearestCard);
        this.nearestCard = null;
        this.lastPosition = null;
        this.reorderAvalibleStores([], true);
      }
    }

    /**
     * Reorder position depens on the list of names.
     *
     * @param {Array<string>} listName
     * @param {boolean} returnPosition
     * @returns {void}
     */
    reorderAvalibleStores(listName, returnPosition = false) {
      if (this.availableStore instanceof HTMLElement) {
        const avalibleStoreCards = this.availableStore.querySelectorAll('.store-card');
        if (returnPosition == true) {
          avalibleStoreCards.forEach((card) => card.style.removeProperty('--order'));
          return;
        }
        avalibleStoreCards.forEach((card) => {
          const storeId = card.getAttribute('data-store-id');
          const index = listName.findIndex((name) => storeId.startsWith(name)) ?? listName.length;
          card.style.setProperty('--order', index);
        });
      }
    }

    /**
     * Find the card for the nearest store.
     *
     * @param {string} storeName - Store name to find.
     * @returns {null | HTMLElement}
     */
    findNearestCardStore(storeName) {
      const storeCards = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.store-card'));

      return [...storeCards].find((card) => {
        const storeId = card.dataset.storeId;
        if (!(typeof storeId === 'string')) return null;

        const normalizedStoreId = storeId.toLocaleLowerCase().trim();
        if (normalizedStoreId.startsWith(storeName)) return card;
      });
    }

    /**
     * Move the nearest card to the target block.
     *
     * @param {HTMLElement} card - Card element to move.
     * @returns {void}
     */
    insertNearestCard(card) {
      let typeStore = /** @type {"available" | "other" | "nearest"} */ ('nearest');
      const target = this.querySelector('#nearest-store');
      if (!target || !card) return;

      /**
       * Show the badge for the current card type.
       *
       * @param {"available" | "other" | "nearest"} type - Badge type.
       * @returns {void}
       */
      const showBadge = (type) => {
        if (['available', 'other', 'nearest'].includes(type)) {
          card
            .querySelector(`.store-card__information-item--${type}`)
            ?.classList.add('store-card__information-item--show');
        }
      };

      /**
       * Read the card type from the parent block.
       *
       * @returns {void}
       */
      const getTypeBadge = () => {
        const mainContainer = card.closest('.postal-container__body-store');
        if (!mainContainer) return;
        const typeMainContainer = mainContainer.getAttribute('id').replace('-store', '');
        if (typeMainContainer) typeStore = typeMainContainer;
      };

      if (target && card) {
        target.querySelector('.store-card')?.remove();
        getTypeBadge();
        showBadge(typeStore);
        target.classList.remove('hidden');
        target.appendChild(card);
      }
    }
  }

  // document.addEventListener(
  //   'postal-code-form:submit',
  //   /** @param {CustomEvent<PostcodeMatch & { coordinates: Coordinates, nearestStore: NearestStore }>} event */
  //   (event) => {
  //     console.log('postcode match', event.detail);
  //   },
  // );

  // document.addEventListener(
  //   'postal-code-form:not-found',
  //   /** @param {CustomEvent<{ code: string, message: string }>} event */
  //   (event) => {
  //     console.error('postcode not found', event.detail);
  //   },
  // );

  // document.addEventListener(
  //   'postal-code-form:error',
  //   /** @param {CustomEvent<{ code: string, message: string }>} event */
  //   (event) => {
  //     console.error('postcode lookup error', event.detail);
  //   },
  // );

  if (!window.customElements.get('postal-code-form')) {
    window.customElements.define('postal-code-form', PostalCodeForm);
  }

  if (!window.customElements.get('postal-code-drawer')) {
    window.customElements.define('postal-code-drawer', PostalCodeDrawer);
  }
});
